import { useQueryClient } from '@tanstack/react-query';
import { ApiError, useApi } from '@/hooks/useApi';
import { useModeAwareMutation } from '@/hooks/useModeAwareQuery';

export interface BulkEmailEntry {
  customerId: number;
  toRecipients: string[];
  ccRecipients?: string[];
  subject: string;
  htmlBody: string;
  templateId?: number;
  templateName?: string;
  isSubjectModified: boolean;
  isBodyModified: boolean;
  attachOutstandingInvoices: boolean;
}

export interface LogBulkCustomerEmailRequest {
  entries: BulkEmailEntry[];
}

export interface LogBulkCustomerEmailResponse {
  successCount: number;
  failedCount: number;
  errors: string[];
}

export function useLogBulkCustomerEmail() {
  const { apiBaseUrl, getHeaders } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<LogBulkCustomerEmailResponse, Error, LogBulkCustomerEmailRequest>(
    async (request) => {
      const headers = await getHeaders();
      const response = await fetch(`${apiBaseUrl}/api/v1/customers/communications/log-bulk-email`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(errorData.message || `HTTP error ${response.status}`, response.status, errorData);
      }

      return response.json();
    },
    () => {
      return { successCount: 0, failedCount: 0, errors: [] };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['customers'] });
        queryClient.invalidateQueries({ queryKey: ['customer-communications'] });
        queryClient.invalidateQueries({ queryKey: ['customer-recent-activities'] });
        queryClient.invalidateQueries({ queryKey: ['customer-audit-logs'] });
      },
    },
  );
}
